import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from './AuthProvider';

// Utility to format date nicely
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString(undefined,{ year:'numeric', month:'long', day:'numeric'}); } catch { return v; }
};

export default function LeadDetails(){
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user has admin access (similar to LeadTasksView.jsx)
  const isAdmin = user && (user.is_superuser || user.role_name === 'Admin' || user.role_name === 'Super Admin' || user.role_name === 'Associate admin');
  const [fullLoading, setFullLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [jobRoles, setJobRoles] = useState([]);
  const [jobRoleSearch, setJobRoleSearch] = useState('');
  const [jobRoleResults, setJobRoleResults] = useState([]);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [fileStatus, setFileStatus] = useState({ resume: 'idle' });
  const [clientDetails, setClientDetails] = useState(null);
  const [fetchingClientDetails, setFetchingClientDetails] = useState(false);
  const [showClientDetailsModal, setShowClientDetailsModal] = useState(false);

  useEffect(()=> {
    let ignore=false;
    const fetchLead = async()=> {
      try {
        // Request a lightweight lead: skip heavy extracted formatting and file URL generation
        const res = await axios.get(`/api/v1/leads/${id}/?format_extracted=false&include_files=false`);
        if(!ignore){ setLead(res.data); setLoading(false); }
      } catch(e){ if(!ignore){ setError('Failed to load lead'); setLoading(false);} }
    };
    // Do not preload full job role list here to avoid large fetches. Use typeahead when editing.
    fetchLead();
    return ()=> { ignore=true; };
  },[id]);

  const fetchFullLead = useCallback(async () => {
    // Fetch a full lead object including formatted extracted data and file URLs.
    try {
      setFullLoading(true);
      const res = await axios.get(`/api/v1/leads/${id}/?format_extracted=true&include_files=true`);
      // Merge returned fields into current lead
      setLead(l => ({ ...(l||{}), ...(res.data || {}) }));
    } catch (e) {
      console.error('Failed to load full lead', e);
    } finally {
      setFullLoading(false);
    }
  }, [id]);

  // Debounced server search for job roles when user types in the edit modal
  useEffect(() => {
    if (!jobRoleSearch || jobRoleSearch.length < 3) {
      setJobRoleResults([]);
      setShowRoleDropdown(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/v1/jobroles/?search=${encodeURIComponent(jobRoleSearch)}&page_size=20`);
        const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setJobRoleResults(data);
        setShowRoleDropdown(true);
      } catch (err) {
        setJobRoleResults([]);
        setShowRoleDropdown(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [jobRoleSearch]);

  // Auto-fetch client details when lead is loaded (silently, for inline display)
  useEffect(() => {
    const fetchClientDetailsAuto = async () => {
      if (!lead?.apwId) return;
      setFetchingClientDetails(true);
      try {
        const res = await axios.get(`https://ticketingtoolapplywizz.vercel.app/api/get-client-details?applywizz_id=${lead.apwId}`);
        setClientDetails(res.data);
      } catch (e) {
        console.error('Failed to auto-fetch client details', e);
        // Silently fail - user can manually retry if needed
      } finally { setFetchingClientDetails(false); }
    };

    if (lead && !clientDetails) {
      fetchClientDetailsAuto();
    }
  }, [lead, clientDetails]);

  // File handling utilities
  const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const thresh = 1024;
    if (bytes < thresh) return bytes + ' B';
    const units = ['KB','MB','GB'];
    let u = -1; let size = bytes;
    do { size /= thresh; ++u; } while (size >= thresh && u < units.length - 1);
    return size.toFixed(1) + ' ' + units[u];
  };

  const selectFile = useCallback((name, file) => {
    console.log('File selected', name, file);
    if (!file) return;
    // Basic validation
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const tooBig = file.size > 10 * 1024 * 1024;
    setFileStatus(prev => ({ ...prev, [name]: 'loading' }));
    setForm(prev => ({
      ...prev,
      [name]: file
    }));
    // Simulate async prep (since actual upload occurs on submit)
    setTimeout(() => {
      if (tooBig || (file.type && !allowed.includes(file.type))) {
        setFileStatus(prev => ({ ...prev, [name]: 'error' }));
      } else {
        setFileStatus(prev => ({ ...prev, [name]: 'success' }));
      }
    }, 400);
  }, []);

  const removeFile = (name) => {
    setForm(prev => ({ ...prev, [name]: null }));
    setFileStatus(prev => ({ ...prev, [name]: 'idle' }));
  };

  const FileUploadBox = ({ label, name, file, status, accept, onSelect, onRemove, helper }) => {
    const [dragging, setDragging] = useState(false);
    const borderColor = status === 'success' ? 'border-green-400' : status === 'error' ? 'border-red-400' : dragging ? 'border-indigo-400' : 'border-gray-300';
    const bgColor = status === 'success' ? 'bg-green-50' : status === 'error' ? 'bg-red-50' : 'bg-white';
    const dashed = status === 'success' ? '' : 'border-dashed';
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div
          className={`relative flex flex-col items-center justify-center px-6 py-10 border-2 ${dashed} rounded-xl transition-colors duration-300 ${borderColor} ${bgColor}`}
          onDragOver={(e)=>{e.preventDefault(); setDragging(true);}}
          onDragLeave={(e)=>{e.preventDefault(); setDragging(false);}}
          onDrop={(e)=>{e.preventDefault(); setDragging(false); if(e.dataTransfer.files?.[0]) onSelect(name, e.dataTransfer.files[0]);}}
        >
          {!file && (
            <>
              <svg className="mx-auto h-14 w-14 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4M32 8h8m-4-4v8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="mt-4 text-sm text-gray-700"><span className="text-indigo-600 font-medium cursor-pointer relative">
                <input
                  type="file"
                  accept={accept}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e)=> e.target.files && onSelect(name, e.target.files[0])}
                />Upload a file</span> or drag and drop</p>
              <p className="mt-2 text-xs text-gray-500">{helper}</p>
            </>
          )}
          {file && (
            <>
              <div className="flex flex-col items-center">
                <div className="h-20 w-20 rounded-2xl bg-indigo-100 flex items-center justify-center mb-4">
                  <svg className="h-10 w-10 text-indigo-600" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"/><path d="M14 3.5V9h5.5" fill="#fff" opacity="0.6"/></svg>
                </div>
                <p className="text-base font-medium text-gray-900 text-center break-all max-w-xs">{file.name}</p>
                <p className="mt-1 text-sm text-gray-500">{formatSize(file.size)}</p>
                <span className={`mt-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${status==='success'?'bg-green-200 text-green-800':status==='error'?'bg-red-200 text-red-800':'bg-indigo-200 text-indigo-800'}`}>
                  {status==='success' && '✅ Ready to upload'}
                  {status==='error' && '⚠️ Invalid file'}
                  {status==='loading' && '⏳ Checking...'}
                  {status==='idle' && 'File selected'}
                </span>
                <button type="button" onClick={()=> onRemove(name)} className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 shadow-sm">
                  <span role="img" aria-label="remove">🗑️</span> Remove File
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const triggerExtraction = async () => {
    if(!lead) return;
    setExtracting(true);
    try {
      const csrf = document.cookie.split('; ').find(r => r.startsWith('csrftoken='))?.split('=')[1];
      const res = await axios.post(`/api/v1/leads/${id}/extract-data/`, {}, csrf? { headers:{'X-CSRFToken': csrf}}: undefined);
      // Merge extracted_data into lead object
      setLead(l => ({ ...l, extracted_data: res.data.extracted_data }));
    } catch(e){
      console.error('Extraction failed', e);
      const msg = e?.response?.data?.error || 'Extraction failed';
      alert(msg);
    } finally { setExtracting(false); }
  };

  const fetchClientDetails = async () => {
    if (!lead?.apwId) {
      alert('No APW ID available for this lead');
      return;
    }
    setFetchingClientDetails(true);
    try {
      const res = await axios.get(`https://ticketingtoolapplywizz.vercel.app/api/get-client-details?applywizz_id=${lead.apwId}`);
      setClientDetails(res.data);
      setShowClientDetailsModal(true);
    } catch (e) {
      console.error('Failed to fetch client details', e);
      const msg = e?.response?.data?.error || 'Failed to fetch client details';
      alert(msg);
    } finally { setFetchingClientDetails(false); }
  };

  const resolveTargetRoleId = (leadObj) => {
    if (!leadObj) return '';
    const extractIdFromObj = (obj) => {
      if (!obj || typeof obj !== 'object') return undefined;
      return obj.id ?? obj.pk ?? obj._id ?? obj.ID ?? obj.Id;
    };
    const extractNameFromObj = (obj) => {
      if (!obj) return null;
      return obj.name ?? obj.title ?? null;
    };

    const nestedId = extractIdFromObj(leadObj.targetRoleId) ?? extractIdFromObj(leadObj.targetRole);
    const nestedName = extractNameFromObj(leadObj.targetRoleId) ?? extractNameFromObj(leadObj.targetRole);

    const findByIdInRoles = (id) => Array.isArray(jobRoles) && jobRoles.find(r => String(r.id) === String(id));
    const findByNameInRoles = (name) => {
      if (!name || !Array.isArray(jobRoles)) return null;
      return jobRoles.find(r => (r.name || '').toLowerCase() === String(name).toLowerCase());
    };

    if (nestedId !== undefined && nestedId !== null) {
      if (findByIdInRoles(nestedId)) return String(nestedId);
      if (nestedName) {
        const jr = findByNameInRoles(nestedName);
        if (jr) return String(jr.id);
      }
    }

    if (leadObj.target_role_id !== undefined && leadObj.target_role_id !== null) {
      if (findByIdInRoles(leadObj.target_role_id)) return String(leadObj.target_role_id);
    }
    if (leadObj.targetRoleId !== undefined && leadObj.targetRoleId !== null) {
      if (findByIdInRoles(leadObj.targetRoleId)) return String(leadObj.targetRoleId);
    }
    if (leadObj.targetRole !== undefined && leadObj.targetRole !== null && (typeof leadObj.targetRole === 'number' || /^\\d+$/.test(String(leadObj.targetRole)))) {
      if (findByIdInRoles(leadObj.targetRole)) return String(leadObj.targetRole);
    }

    const nameCandidates = [nestedName, leadObj.targetRoleName, leadObj.target_role_name, (typeof leadObj.targetRole === 'string' ? leadObj.targetRole : null)].filter(Boolean);
    for (const name of nameCandidates) {
      const jr = findByNameInRoles(name);
      if (jr) return String(jr.id);
    }

    return '';
  };

  const openEdit = () => {
    if(!lead) return;
    const clientPreferencesArray = lead.clientPreferences 
      ? Object.entries(lead.clientPreferences).map(([k, v]) => ({
          key: k,
          value: Array.isArray(v) ? v.join(', ') : String(v)
        }))
      : [];

    setForm({
      name: lead.name||'',
      email: lead.email||'',
      target_role_id: resolveTargetRoleId(lead),
      target_role_name: lead.targetRoleId?.name || lead.targetRoleName || lead.targetRole || '',
      yearsExp: lead.yearsExp ?? '',
      location: lead.location||'',
      country: lead.country||'United States of America',
      status: lead.status||'In Progress',
      workAuthorization: lead.workAuthorization||'Citizen',
      workPreference: lead.workPreference||'All',
      gender: lead.gender||'',
      plan: lead.plan||'Basic',
      startDate: lead.startDate?.split('T')[0] || '',
      endDate: lead.endDate?.split('T')[0] || '',
      expectedSalaryRange: lead.expectedSalaryRange||'',
      number_of_applications: lead.number_of_applications||'',
      githubUrl: lead.githubUrl||'',
      linkedinUrl: lead.linkedinUrl||'',
      resumeLink: lead.resumeLink||'',
      google_drive_resume_link: lead.google_drive_resume_link||'',
      willingToRelocate: lead.willingToRelocate||'Yes',
      sponsorship: lead.sponsorship||'',
      clientPreferences: clientPreferencesArray.length > 0 ? clientPreferencesArray : [{ key: '', value: '' }],
      resume: null
    });
    setFileStatus({ resume: 'idle' });
    setShowEdit(true);
  };

  const handleSave = async () => {
    // Ensure a target role is selected unless the lead already has a current role available.
    // Some leads arrive with a nested `targetRoleId` or `targetRoleName` provided by the API.
    const hasExistingRole = !!(lead?.targetRoleId?.name || lead?.targetRoleName || lead?.targetRole);
    if ((!form.target_role_id || form.target_role_id === '') && !hasExistingRole) {
      alert('Please select a Target Role before saving.');
      return;
    }

    // Reconstruct clientPreferences from key-value array
    const parsedClientPreferences = {};
    if (Array.isArray(form.clientPreferences)) {
      form.clientPreferences.forEach(pref => {
        const k = pref.key ? pref.key.trim() : '';
        const v = pref.value ? pref.value.trim() : '';
        if (k) {
          if (v.toLowerCase() === 'true') {
            parsedClientPreferences[k] = true;
          } else if (v.toLowerCase() === 'false') {
            parsedClientPreferences[k] = false;
          } else if (v.includes(',')) {
            parsedClientPreferences[k] = v.split(',').map(item => item.trim()).filter(Boolean);
          } else {
            parsedClientPreferences[k] = v;
          }
        }
      });
    }

    setSaving(true);
    try {
      // Check if we have files to upload
      const hasFiles = form.resume;

      if (hasFiles) {
        // Use FormData for file uploads (requires PUT/POST with multipart/form-data)
        const formDataToSend = new FormData();

        // Add all form fields except files
        Object.keys(form).forEach(key => {
          const value = form[key];
          if (value !== null && value !== undefined && value !== '' && key !== 'resume') {
            if (key === 'target_role_id') {
              formDataToSend.append('target_role_id', value);
            } else if (key === 'yearsExp') {
              // Ensure yearsExp is an integer
              const intValue = parseInt(value, 10);
              if (!isNaN(intValue)) {
                formDataToSend.append(key, intValue);
              }
            } else if (key === 'clientPreferences') {
              formDataToSend.append('clientPreferences', JSON.stringify(parsedClientPreferences));
            } else {
              formDataToSend.append(key, value);
            }
          }
        });

        // Add resume file if present
        if (form.resume) {
          formDataToSend.append('resume', form.resume);
        }

        const csrf = document.cookie.split('; ').find(r => r.startsWith('csrftoken='))?.split('=')[1];
        const res = await axios.put(`/api/v1/leads/${id}/`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(csrf && { 'X-CSRFToken': csrf })
          }
        });

        let updated = res.data;
        if (form.target_role_id && updated && !updated.targetRoleId) {
          const jr = { id: form.target_role_id, name: form.target_role_name || lead.targetRoleId?.name || lead.targetRoleName };
          updated = { ...updated, targetRoleId: jr };
        }
        setLead(updated);
      } else {
        // No files - use regular JSON PATCH
        const payload = { ...form };
        // Remove file field from payload
        delete payload.resume;
        payload.clientPreferences = parsedClientPreferences;

        payload.willingToRelocate = form.willingToRelocate === 'true';
        // Ensure yearsExp is an integer if present
        if (payload.yearsExp !== undefined && payload.yearsExp !== null && payload.yearsExp !== '') {
          payload.yearsExp = parseInt(payload.yearsExp, 10);
        }
        // Forward target_role_id only if selected (avoid sending empty string)
        if (payload.target_role_id === '' || payload.target_role_id === undefined) {
          delete payload.target_role_id;
        }
        // Convert endDate empty string to null (field allows null) so serializer accepts it
        if (payload.endDate === '') payload.endDate = null;
        // Remove empty string date for startDate (avoid attempting to blank a required field)
        if (payload.startDate === '') delete payload.startDate;
        // For optional URL/text fields convert '' to null to satisfy validators that disallow blank
        ['resumeLink','personalDetailsUrl','githubUrl','linkedinUrl','expectedSalaryRange','google_drive_resume_link','sponsorship'].forEach(f=> {
          if (payload[f] === '') payload[f] = null;
        });
        // Drop any remaining empty-string fields to leverage partial update semantics
        Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
        const csrf = document.cookie.split('; ').find(r => r.startsWith('csrftoken='))?.split('=')[1];
        const res = await axios.patch(`/api/v1/leads/${id}/`, payload, csrf? { headers:{'X-CSRFToken': csrf}}: undefined);
        let updated = res.data;
        // If server didn't return nested targetRoleId, construct a minimal nested object so UI can continue to show role name
        if (payload.target_role_id && updated && !updated.targetRoleId) {
          const jr = { id: payload.target_role_id, name: form.target_role_name || lead.targetRoleId?.name || lead.targetRoleName };
          updated = { ...updated, targetRoleId: jr };
        }
        setLead(updated);
      }

      setShowEdit(false);
    } catch(e){
      console.error('Lead save failed', e);
      const serverMsg = e?.response?.data ? JSON.stringify(e.response.data) : 'Save failed';
      alert(serverMsg);
    }
    finally { setSaving(false); }
  };

  if(loading) return <div className="p-8">Loading...</div>;
  if(error) return <div className="p-8 text-red-600">{error}</div>;
  if(!lead) return null;

  const docItems = [
    { key:'resume', label:'Resume', url: lead.resumeUrl, filename: lead.resume_original_filename, s3: lead.resume_s3_path },
    { key:'personal', label:'Personal Details', url: lead.personalDetailsUrl, filename: lead.personal_details_original_filename, s3: lead.personal_details_s3_path }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white px-6 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Lead Details</h1>
            <p className="text-gray-600 mt-1">Comprehensive information for {lead.name}</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/leads')} className="inline-flex items-center px-4 py-2 rounded-md border bg-white text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">← Back to Leads</button>
              <button onClick={openEdit} className="inline-flex items-center px-4 py-2 rounded-md border bg-indigo-600 text-sm font-medium text-white shadow-sm hover:bg-indigo-700">✏️ Edit Lead</button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <InfoCard lead={lead} />
            {Array.isArray(lead.alternateJobRoles) && lead.alternateJobRoles.length>0 && <TagCard title="Alternate Job Roles" color="blue" items={lead.alternateJobRoles} />}
            {Array.isArray(lead.excludeCompanies) && lead.excludeCompanies.length>0 && <TagCard title="Excluded Companies" color="red" items={lead.excludeCompanies} />}
            {(() => {
              const sv = lead.servicesOpted;
              if (!sv) return null;
              // If string, treat as empty if blank or '{}' after trimming
              if (typeof sv === 'string') {
                const trimmed = sv.trim();
                if (!trimmed || trimmed === '{}' || trimmed === '[]') return null;
                return <ServicesCard services={sv} />;
              }
              // If object, show only when it has at least one own key
              if (typeof sv === 'object' && Object.keys(sv).length === 0) return null;
              return <ServicesCard services={sv} />;
            })()}
            <ExtractedCard lead={lead} fetchFullLead={fetchFullLead} fullLoading={fullLoading} />

            {/* Client Details Section - Displayed inline */}
            {(clientDetails || (fetchingClientDetails && lead?.apwId)) && (
              <ClientDetailsSection
                data={clientDetails}
                loading={fetchingClientDetails && !clientDetails}
              />
            )}
          </div>
          <div className="space-y-6">
            <DocumentsCard items={docItems} fetchFullLead={fetchFullLead} fullLoading={fullLoading} />
            <TimelineCard lead={lead} />
            <ActionsCard onEdit={openEdit} onExtract={triggerExtraction} hasExtracted={!!lead.extracted_data} extracting={extracting} onFetchClientDetails={fetchClientDetails} fetchingClientDetails={fetchingClientDetails} />
          </div>
        </div>
      </div>
      {showEdit && (
        <EditModal
          lead={lead}
          form={form}
          jobRoles={jobRoles}
          saving={saving}
          onChange={e=> setForm(f=> ({...f,[e.target.name]: e.target.value}))}
          onClose={()=> setShowEdit(false)}
          onSave={handleSave}
          jobRoleSearch={jobRoleSearch}
          setJobRoleSearch={setJobRoleSearch}
          jobRoleResults={jobRoleResults}
          showRoleDropdown={showRoleDropdown}
          setShowRoleDropdown={setShowRoleDropdown}
          setForm={setForm}
          fileStatus={fileStatus}
          selectFile={selectFile}
          removeFile={removeFile}
          FileUploadBox={FileUploadBox}
        />
      )}
      {showClientDetailsModal && clientDetails && (
        <ClientDetailsModal
          data={clientDetails}
          onClose={() => setShowClientDetailsModal(false)}
        />
      )}
    </div>
  );
}

function InfoCard({ lead }) {
  const rows = [
    ['Name', lead.name],
    ['Email', lead.email],
    ['Target Role', lead.targetRoleId?.name || lead.targetRoleName || '—'],
    ['Alternate Roles', lead.alternateRoles && lead.alternateRoles.length > 0 ? lead.alternateRoles.join(', ') : '—'],
    ['Job Titles To Apply For', lead.jobTitlesToApplyFor && lead.jobTitlesToApplyFor.length > 0 ? lead.jobTitlesToApplyFor.join(', ') : '—'],
    ['Client Preferences', lead.clientPreferences && Object.keys(lead.clientPreferences).length > 0 ? (
      <div className="text-xs space-y-1">
        {Object.entries(lead.clientPreferences).map(([key, val]) => (
          <div key={key}>
            <span className="font-semibold text-gray-700">{key.replace(/_/g, ' ')}:</span>{' '}
            {Array.isArray(val) ? val.join(', ') : typeof val === 'object' ? JSON.stringify(val) : String(val)}
          </div>
        ))}
      </div>
    ) : '—'],
    ['Experience', lead.yearsExp != null ? `${lead.yearsExp} year${lead.yearsExp===1?'':'s'}`:'—'],
    ['Location', lead.location||'—'],
    ['Country', lead.country||'—'],
    ['Status', lead.status||'New'],
    ['Plan', lead.plan||'—'],
    ['Work Authorization', lead.workAuthorization||'—'],
    ['Work Preference', lead.workPreference||'—'],
    ['Gender', lead.gender||'—'],
    ['Relocate', lead.willingToRelocate? 'Yes':'No'],
    ['Start Date', fmtDate(lead.startDate)],
    ['End Date', fmtDate(lead.endDate)],
    ['Expected Salary', lead.expectedSalaryRange||'—'],
    ['Applications Chosen', lead.number_of_applications||'NA'],
    ['GitHub', lead.githubUrl ? <a href={lead.githubUrl} target="_blank" rel="noreferrer" className="text-indigo-600 underline">Link</a> : '—'],
    ['LinkedIn', lead.linkedinUrl ? <a href={lead.linkedinUrl} target="_blank" rel="noreferrer" className="text-indigo-600 underline">Link</a> : '—'],
    ['Google Drive Resume', lead.google_drive_resume_link ? <a href={lead.google_drive_resume_link} target="_blank" rel="noreferrer" className="text-indigo-600 underline">Link</a> : '—'],
    ['Willing to Relocate', lead.willingToRelocate||'—'],
    ['Sponsorship Required', lead.sponsorship||'—'],
  ];
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="border-b bg-gradient-to-r from-indigo-50 via-white to-white px-6 py-4"><h2 className="text-xl font-semibold">Basic Information</h2></div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {rows.map(([label,val])=> (
          <div key={label}>
            <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
            <p className="text-gray-900 text-sm font-medium break-words">{val || '—'}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TagCard({ title, color, items }) {
  const colorMap = {
    blue:'bg-blue-100 text-blue-800',
    red:'bg-red-100 text-red-800',
    green:'bg-green-100 text-green-800'
  };
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="border-b bg-gradient-to-r from-gray-50 via-white to-white px-6 py-4"><h2 className="text-xl font-semibold">{title}</h2></div>
      <div className="p-6 flex flex-wrap gap-2">
        {items.map(it=> <span key={it} className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[color]}`}>{it}</span>)}
      </div>
    </div>
  );
}

function ServicesCard({ services }) {
  let parsed = services;
  if (typeof services === 'string') { try { parsed = JSON.parse(services); } catch { /* ignore */ } }
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="border-b bg-gradient-to-r from-green-50 via-white to-white px-6 py-4"><h2 className="text-xl font-semibold">Services Opted</h2></div>
      <div className="p-6">
        <pre className="text-xs bg-gray-50 rounded-md p-4 overflow-auto max-h-64 whitespace-pre-wrap">{JSON.stringify(parsed, null, 2)}</pre>
      </div>
    </div>
  );
}

function ExtractedCard({ lead, fetchFullLead, fullLoading }) {
  const data = lead.extracted_data || lead.extractedData;
  if (!data) return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900">Extracted Information</h2>
        <p className="text-sm text-gray-500 mt-1">Data extracted from resume and personal details files.</p>
      </div>
      <div className="p-6 text-center text-gray-500">No extracted data available yet.</div>
    </div>
  );

  // If extracted data exists but we saved only raw fields, offer to load full formatted data
  const isLightweight = !data.formatted_display && !data.formatted_summary && !data.formatted_work_experience;
  if (isLightweight) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Extracted Information</h2>
          <p className="text-sm text-gray-500 mt-1">Lightweight extracted data is available.</p>
        </div>
        <div className="p-6 text-center">
          <p className="text-sm text-gray-700 mb-4">A formatted view (skills, summary, experience) is available but not loaded to save time.</p>
          <div className="flex justify-center gap-3">
            <button onClick={fetchFullLead} disabled={fullLoading} className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium">{fullLoading ? 'Loading…' : 'Load formatted extracted data'}</button>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to render a simple field
  const renderField = (label, value) => {
    if (!value) return null;
    return (
      <div className="mb-4">
        <dt className="text-sm font-medium text-gray-600 mb-1">{label}</dt>
        <dd className="text-sm text-gray-900">{value}</dd>
      </div>
    );
  };

  // Helper function to render skills as badges
  const renderSkills = (skills) => {
    if (!skills) return null;

    let skillsList = [];

    // Handle enhanced serializer format
    if (skills && typeof skills === 'object' && skills.formatted_list) {
      skillsList = skills.formatted_list;
    }
    // Handle array format
    else if (Array.isArray(skills)) {
      skillsList = skills;
    }
    // Handle string format
    else if (typeof skills === 'string') {
      skillsList = skills.split(',').map(s => s.trim()).filter(s => s);
    }

    if (skillsList.length === 0) return null;

    return (
      <div className="mb-4">
        <dt className="text-sm font-medium text-gray-600 mb-2">Skills</dt>
        <dd className="flex flex-wrap gap-1">
          {skillsList.map((skill, idx) => (
            <span
              key={idx}
              className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"
            >
              {skill}
            </span>
          ))}
        </dd>
      </div>
    );
  };

  // Helper function to render work experience
  const renderWorkExperience = (workExp) => {
    if (!workExp) return null;

    let experienceList = [];

    // Handle enhanced serializer format
    if (workExp && typeof workExp === 'object' && workExp.formatted) {
      experienceList = Array.isArray(workExp.formatted) ? workExp.formatted : [workExp.formatted];
    }
    // Handle array format
    else if (Array.isArray(workExp)) {
      experienceList = workExp;
    }
    // Handle string format (try to parse JSON or extract information)
    else if (typeof workExp === 'string') {
      try {
        const parsed = JSON.parse(workExp);
        experienceList = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        // If not valid JSON, try to extract information from the text format
        if (workExp.includes("'company':") || workExp.includes('"company":')) {
          // Parse the object-like string format from Nova Lite
          try {
            // Try to convert single quotes to double quotes and parse
            const cleanedWorkExp = workExp
              .replace(/'/g, '"')
              .replace(/\{/g, '{"')
              .replace(/"/g, '"')
              .replace(/: /g, '": "')
              .replace(/", "/g, '", "')
              .replace(/"\}/g, '"}');

            // Try a more robust parsing approach
            const matches = workExp.match(/'company':\s*'([^']*)'.*?'position':\s*'([^']*)'.*?'duration':\s*'([^']*)'.*?'responsibilities':\s*'([^']*)'/);
            if (matches) {
              experienceList = [{
                company: matches[1],
                position: matches[2],
                duration: matches[3],
                responsibilities: matches[4]
              }];
            } else {
              // Fallback to displaying as formatted text
              return (
                <div className="mb-6">
                  <dt className="text-sm font-medium text-gray-600 mb-3">Work Experience</dt>
                  <dd className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                      {workExp.replace(/[{}]/g, '').replace(/'/g, '').replace(/,\s*/g, '\n')}
                    </div>
                  </dd>
                </div>
              );
            }
          } catch (parseError) {
            // If all parsing fails, display as formatted text
            return (
              <div className="mb-6">
                <dt className="text-sm font-medium text-gray-600 mb-3">Work Experience</dt>
                <dd className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                    {workExp}
                  </div>
                </dd>
              </div>
            );
          }
        } else {
          // Regular text format
          return (
            <div className="mb-6">
              <dt className="text-sm font-medium text-gray-600 mb-2">Work Experience</dt>
              <dd className="text-sm text-gray-900 whitespace-pre-wrap">{workExp}</dd>
            </div>
          );
        }
      }
    }

    if (experienceList.length === 0) return null;

    return (
      <div className="mb-6">
        <dt className="text-sm font-medium text-gray-600 mb-3">Work Experience</dt>
        <dd className="space-y-4">
          {experienceList.map((exp, idx) => (
            <div key={idx} className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  {exp.position || exp.title || 'Position'}
                </h4>
                <p className="text-sm text-gray-700">
                  {exp.company} {exp.duration && `• ${exp.duration}`}
                </p>
              </div>
              {exp.responsibilities && (
                <div className="text-sm text-gray-600">
                  {typeof exp.responsibilities === 'string' ? (
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {exp.responsibilities.split('.').filter(s => s.trim()).map((sentence, sIdx) => (
                        sentence.trim() && (
                          <div key={sIdx} className="mb-2">
                            • {sentence.trim()}.
                          </div>
                        )
                      ))}
                    </div>
                  ) : Array.isArray(exp.responsibilities) ? (
                    <ul className="list-disc list-inside space-y-1">
                      {exp.responsibilities.map((resp, respIdx) => (
                        <li key={respIdx}>{resp}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>{exp.responsibilities}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </dd>
      </div>
    );
  };

  const renderEducation = () => {
    // Use formatted education data if available, otherwise fall back to raw data
    const educationData = data.formatted_education?.formatted || data.education;

    if (!educationData) return null;

    // If it's already an array (from formatted data), use it directly
    if (Array.isArray(educationData)) {
      return (
        <div className="mb-6">
          <dt className="text-sm font-medium text-gray-600 mb-3">Education</dt>
          <dd className="space-y-3">
            {educationData.map((edu, idx) => (
              <div key={idx} className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <div className="mb-1">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {edu.degree || 'Degree'}
                  </h4>
                </div>
                <div className="text-sm text-gray-700">
                  {edu.institution}
                  {edu.year && ` • ${edu.year}`}
                  {edu.GPA && ` • GPA: ${edu.GPA}`}
                  {edu.percentage && ` • ${edu.percentage}`}
                </div>
                {edu.location && (
                  <div className="text-xs text-gray-600 mt-1">{edu.location}</div>
                )}
              </div>
            ))}
          </dd>
        </div>
      );
    }

    // If it's a string, try to parse it as JSON
    if (typeof educationData === 'string') {
      try {
        const parsed = JSON.parse(educationData);
        if (Array.isArray(parsed)) {
          return (
            <div className="mb-6">
              <dt className="text-sm font-medium text-gray-600 mb-3">Education</dt>
              <dd className="space-y-3">
                {parsed.map((edu, idx) => (
                  <div key={idx} className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                    <div className="mb-1">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {edu.degree || 'Degree'}
                      </h4>
                    </div>
                    <div className="text-sm text-gray-700">
                      {edu.institution}
                      {edu.year && ` • ${edu.year}`}
                      {edu.GPA && ` • GPA: ${edu.GPA}`}
                      {edu.percentage && ` • ${edu.percentage}`}
                    </div>
                    {edu.location && (
                      <div className="text-xs text-gray-600 mt-1">{edu.location}</div>
                    )}
                  </div>
                ))}
              </dd>
            </div>
          );
        }
      } catch (parseError) {
        // If parsing fails, display as formatted text
        return (
          <div className="mb-6">
            <dt className="text-sm font-medium text-gray-600 mb-3">Education</dt>
            <dd className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                {educationData}
              </div>
            </dd>
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900">Extracted Information</h2>
        <p className="text-sm text-gray-500 mt-1">Data extracted from resume and personal details files.</p>
      </div>
      <div className="p-6">
        <dl className="grid grid-cols-1 gap-4">
          {/* Basic Information */}
          {renderField('Full name', data.fullName || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : null))}
          {renderField('Email', data.email)}
          {renderField('Phone', data.phone)}
          {renderField('Address', data.address)}
          {renderField('Location', data.location)}

          {/* LinkedIn */}
          {data.linkedInUrl && (
            <div className="mb-4">
              <dt className="text-sm font-medium text-gray-600 mb-1">LinkedIn</dt>
              <dd>
                <a
                  href={data.linkedInUrl.startsWith('http') ? data.linkedInUrl : `https://${data.linkedInUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                >
                  {data.linkedInUrl}
                </a>
              </dd>
            </div>
          )}

          {/* Skills */}
          {renderSkills(data.skills)}

          {/* Date of Birth */}
          {renderField('Date of Birth', data.dateOfBirth)}
          {renderField('Age', data.age)}

          {/* Overall Summary */}
          {data.overallSummary && (
            <div className="mb-4">
              <dt className="text-sm font-medium text-gray-600 mb-2">Overall Summary</dt>
              <dd className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                {typeof data.overallSummary === 'object' && data.overallSummary.formatted
                  ? data.overallSummary.formatted
                  : data.overallSummary
                }
              </dd>
            </div>
          )}

          {/* Work Experience */}
          {renderWorkExperience(data.formatted_work_experience?.formatted || data.workExperience)}

          {/* Education */}
          {renderEducation()}

          {/* Certifications */}
          {data.certifications && (
            <div className="mb-4">
              <dt className="text-sm font-medium text-gray-600 mb-2">Certifications</dt>
              <dd>
                {typeof data.certifications === 'string' ? (
                  <div className="flex flex-wrap gap-1">
                    {data.certifications.split(',').map((cert, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded"
                      >
                        {cert.trim()}
                      </span>
                    ))}
                  </div>
                ) : Array.isArray(data.certifications) ? (
                  <div className="flex flex-wrap gap-1">
                    {data.certifications.map((cert, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded"
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-900">{data.certifications}</div>
                )}
              </dd>
            </div>
          )}

          {/* Additional fields */}
          {renderField('Languages', data.languages)}
          {renderField('Key Project Highlights', data.keyProjectHighlights)}
          {renderField('Core Competencies', data.coreCompetencies)}
        </dl>
      </div>
    </div>
  );
}

function DocumentsCard({ items, fetchFullLead, fullLoading }) {
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="border-b bg-gradient-to-r from-gray-50 via-white to-white px-6 py-4"><h2 className="text-xl font-semibold">Documents</h2></div>
      <div className="p-6 space-y-4">
        {items.filter(d=> d.url || d.s3).length ===0 && <div className="text-center text-sm text-gray-500 py-6">No documents uploaded</div>}
        {items.map(doc => (doc.url || doc.s3) && (
          <div key={doc.key} className="border rounded-lg p-4 flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-gray-900">{doc.label}</h5>
            </div>
            <div>
              {doc.url ? (
                <a href={doc.url} target="_blank" rel="noreferrer" className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200">Download</a>
              ) : (
                <button onClick={fetchFullLead} disabled={fullLoading} className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                  {fullLoading ? 'Loading…' : 'Load download link'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TimelineCard({ lead }) {
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="border-b bg-gradient-to-r from-gray-50 via-white to-white px-6 py-4"><h2 className="text-xl font-semibold">Timeline</h2></div>
      <div className="p-6 space-y-4 text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Created:</span><span className="text-gray-900">{fmtDate(lead.created_at)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Last Updated:</span><span className="text-gray-900">{fmtDate(lead.updated_at)}</span></div>
      </div>
    </div>
  );
}
function ActionsCard({ onEdit, onExtract, hasExtracted, extracting, onFetchClientDetails, fetchingClientDetails }) {
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="border-b bg-gradient-to-r from-gray-50 via-white to-white px-6 py-4"><h2 className="text-xl font-semibold">Actions</h2></div>
      <div className="p-6 space-y-3">

        <button disabled className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-400 cursor-not-allowed">Share (Soon)</button>
        {!hasExtracted && <button onClick={onExtract} disabled={extracting} className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">{extracting ? 'Extracting...' : 'Extract Data'}</button>}
        {hasExtracted && <button onClick={onExtract} disabled={extracting} className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-indigo-50 text-indigo-700 text-sm font-medium border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50">{extracting ? 'Re-extracting...' : 'Re-Extract Data'}</button>}
        {/* <button onClick={onFetchClientDetails} disabled={fetchingClientDetails} className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium shadow-sm hover:bg-blue-700 disabled:opacity-50"> */}
        {/* {fetchingClientDetails ? 'Fetching...' : '📋 Fetch Client Details'} */}
        {/* </button> */}
      </div>
    </div>
  );
}

function EditModal({ lead, form, jobRoles, saving, onChange, onClose, onSave, jobRoleSearch, setJobRoleSearch, jobRoleResults, showRoleDropdown, setShowRoleDropdown, setForm, fileStatus, selectFile, removeFile, FileUploadBox }) {
  const handlePreferenceChange = (index, field, val) => {
    setForm(prev => {
      const updated = [...(prev.clientPreferences || [])];
      updated[index] = { ...updated[index], [field]: val };
      return { ...prev, clientPreferences: updated };
    });
  };

  const addPreference = () => {
    setForm(prev => ({
      ...prev,
      clientPreferences: [...(prev.clientPreferences || []), { key: '', value: '' }]
    }));
  };

  const removePreference = (index) => {
    setForm(prev => {
      const updated = (prev.clientPreferences || []).filter((_, i) => i !== index);
      return { ...prev, clientPreferences: updated.length > 0 ? updated : [{ key: '', value: '' }] };
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center p-4 bg-gray-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-xl border border-gray-200 relative">
        <form onSubmit={(e)=> { e.preventDefault(); onSave(); }} className="p-6 space-y-6 w-full">
          <h2 className="text-xl font-semibold text-gray-900">{lead?.name || 'Edit Lead'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700">Name *</label>
              <input name="name" value={form.name||''} onChange={onChange} required className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email *</label>
              <input name="email" type="email" value={form.email||''} disabled className="mt-1 w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-2 cursor-not-allowed" />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Target Role *</label>
              <div className="mt-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600">Current Role</label>
                    <input type="text" value={form.target_role_name || ''} disabled className="mt-1 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Current Role ID</label>
                    <input type="text" value={form.target_role_id || ''} disabled className="mt-1 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm" />
                  </div>
                </div>

                <div className="relative">
                  <input
                    name="jobRoleSearch"
                    value={jobRoleSearch}
                    onChange={(e) => { setJobRoleSearch(e.target.value); /* clear selection when typing new search */ setForm(f => ({ ...f, target_role_id: '' })); }}
                    placeholder="Type to search roles (3+ chars)..."
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />

                  {form.target_role_id && !jobRoleResults.some?.(r => String(r.id) === String(form.target_role_id)) && (
                    <div className="mt-2 text-sm text-gray-700">Selected: <span className="font-medium">{form.target_role_name || `Role ${form.target_role_id}`}</span></div>
                  )}

                  {showRoleDropdown && (
                    <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
                      {jobRoleResults.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500">No role matches</div>
                      ) : jobRoleResults.map(r => (
                        <button key={r.id} type="button" onClick={() => {
                          setForm(f => ({ ...f, target_role_id: String(r.id), target_role_name: r.name }));
                          setJobRoleSearch(r.name);
                          setShowRoleDropdown(false);
                        }} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">
                          {r.name}
                        </button>
                      ))}
                    </div>
                  )}

                  <select name="target_role_id" value={form.target_role_id||''} onChange={onChange} className="hidden" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Experience (years) *</label>
              <input name="yearsExp" type="number" min="0" value={form.yearsExp||''} onChange={onChange} required className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Location *</label>
              <input name="location" value={form.location||''} onChange={onChange} required className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Country</label>
              <input name="country" value={form.country||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select name="status" value={form.status||'In Progress'} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {['In Progress','Paused','Completed'].map(s=> <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Work Authorization</label>
              <select name="workAuthorization" value={form.workAuthorization||'Citizen'} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {['Citizen','Green Card','H1B','F1', 'Post-Study Work Visa', 'H4EAD','Other'].map(w=> <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Work Preference</label>
              <select name="workPreference" value={form.workPreference||'All'} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {['Remote','Hybrid','On-site','All'].map(w=> <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Gender</label>
              <select name="gender" value={form.gender||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {['','Male','Female','Other','Prefer not to say'].map(g=> <option key={g} value={g}>{g||'Select'}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Start Date</label>
              <input name="startDate" type="date" value={form.startDate||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">End Date</label>
              <input name="endDate" type="date" value={form.endDate||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Expected Salary Range</label>
              <input name="expectedSalaryRange" value={form.expectedSalaryRange||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Number of Applications</label>
              <select name="number_of_applications" value={form.number_of_applications||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">Select Number of Applications</option>
                <option value="20+">20+</option>
                <option value="40+">40+</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">GitHub URL</label>
              <input name="githubUrl" value={form.githubUrl||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">LinkedIn URL</label>
              <input name="linkedinUrl" value={form.linkedinUrl||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Google Drive Resume Link</label>
              <input name="google_drive_resume_link" value={form.google_drive_resume_link||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="https://drive.google.com/file/d/..." />
            </div>
            <div className="md:col-span-2 border border-gray-200 rounded-lg p-4 bg-gray-50/50">
              <label className="text-sm font-semibold text-gray-800 block mb-1">Client Preferences</label>
              <p className="text-xs text-gray-500 mb-3">Add preferences or details (e.g. key: <code>excluded_companies</code>, value: <code>Google, Meta</code>)</p>
              
              <div className="space-y-3">
                {(form.clientPreferences || []).map((pref, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Preference Key (e.g., excluded_companies)"
                        value={pref.key}
                        onChange={(e) => handlePreferenceChange(idx, 'key', e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Value (e.g. Meta or true)"
                        value={pref.value}
                        onChange={(e) => handlePreferenceChange(idx, 'value', e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePreference(idx)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              
              <button
                type="button"
                onClick={addPreference}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add Preference
              </button>
            </div>

            <div className="md:col-span-2">
              <FileUploadBox
                label="Resume (PDF, Word, or Text)"
                name="resume"
                file={form.resume}
                status={fileStatus.resume}
                accept=".pdf,.doc,.docx,.txt"
                onSelect={selectFile}
                onRemove={removeFile}
                helper="PDF, DOC, DOCX, TXT up to 10MB"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Willing to Relocate?</label>
              <select name="willingToRelocate" value={form.willingToRelocate||'No'} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Sponsorship Required?</label>
              <select name="sponsorship" value={form.sponsorship||''} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">Select Option</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2 rounded-md bg-indigo-600 text-white font-medium shadow hover:bg-indigo-700 disabled:opacity-50">{saving? 'Saving...':'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Inline Client Details Section with Tabs
function ClientDetailsSection({ data, loading }) {
  const [activeTab, setActiveTab] = useState('client');
  const client = data?.client;
  const additionalInfo = data?.additional_information;

  // Show loading skeleton
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="border-b bg-gradient-to-r from-indigo-50 via-white to-white px-6 py-4">
          <h2 className="text-xl font-semibold">External Client Details</h2>
          <p className="text-sm text-gray-500 mt-1">Loading client information...</p>
        </div>
        <div className="p-6">
          <div className="animate-pulse space-y-6">
            {/* Loading skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(9)].map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-5 bg-gray-300 rounded w-3/4"></div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-6 bg-blue-100 rounded-full w-24"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!client) return null;

  const renderField = (label, value) => (
    <div className="mb-4">
      <dt className="text-sm font-medium text-gray-600 mb-1">{label}</dt>
      <dd className="text-sm text-gray-900">{value || '—'}</dd>
    </div>
  );

  const renderArray = (label, arr) => {
    if (!arr || arr.length === 0) return null;
    return (
      <div className="mb-4">
        <dt className="text-sm font-medium text-gray-600 mb-2">{label}</dt>
        <dd className="flex flex-wrap gap-1">
          {arr.map((item, idx) => (
            <span key={idx} className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
              {item}
            </span>
          ))}
        </dd>
      </div>
    );
  };

  const renderResume = (label, resumePath) => {
    if (!resumePath) return null;
    const fullUrl = `https://applywizz-prod.s3.us-east-2.amazonaws.com/${resumePath}`;
    return (
      <div className="mb-4">
        <dt className="text-sm font-medium text-gray-600 mb-1">{label}</dt>
        <dd>
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
          >
            View Resume
          </a>
        </dd>
      </div>
    );
  };

  const tabs = [
    { id: 'client', label: 'Client Information', icon: '👤', color: 'indigo' },
    { id: 'additional', label: 'Additional Information', icon: '📋', color: 'green', disabled: !additionalInfo },
    { id: 'eligibility', label: 'Eligibility & Authorization', icon: '✅', color: 'yellow', disabled: !additionalInfo },
    { id: 'links', label: 'Links & Services', icon: '🔗', color: 'purple', disabled: !additionalInfo }
  ];

  const getTabButtonClass = (tab) => {
    const baseClass = "relative px-6 py-3 text-sm font-medium transition-all duration-200 border-b-2 focus:outline-none";

    if (tab.disabled) {
      return `${baseClass} border-transparent text-gray-400 cursor-not-allowed`;
    }

    if (activeTab === tab.id) {
      const colorMap = {
        indigo: 'border-indigo-500 text-indigo-700',
        green: 'border-green-500 text-green-700',
        yellow: 'border-yellow-500 text-yellow-700',
        purple: 'border-purple-500 text-purple-700'
      };
      return `${baseClass} ${colorMap[tab.color]} bg-${tab.color}-50/50`;
    }

    return `${baseClass} border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300`;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="border-b bg-gradient-to-r from-indigo-50 via-white to-white px-6 py-4">
        <h2 className="text-xl font-semibold">External Client Details</h2>
        <p className="text-sm text-gray-500 mt-1">Data from Customer Dashboard tool (ID: {client.applywizz_id})</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 bg-gray-50">
        <nav className="flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              className={getTabButtonClass(tab)}
              disabled={tab.disabled}
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {/* Client Information Tab */}
        {activeTab === 'client' && (
          <div className="animate-fadeIn">
            <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-6 border border-indigo-100">
              <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderField('Full Name', client.full_name)}
                {renderField('Personal Email', client.personal_email)}
                {renderField('Company Email', client.company_email)}
                {renderField('WhatsApp', client.whatsapp_number)}
                {renderField('Phone', client.callable_phone)}
                {renderField('Visa Type', client.visa_type)}
                {renderField('Sponsorship', client.sponsorship ? 'Yes' : 'No')}
                {renderField('Work Authorization', client.work_auth_details)}
                {renderField('Salary Range', client.salary_range)}
                {renderField('Created At', new Date(client.created_at).toLocaleString())}
              </dl>
              <dl className="mt-4">
                {renderArray('Job Role Preferences', client.job_role_preferences)}
                {renderArray('Location Preferences', client.location_preferences)}
              </dl>
            </div>
          </div>
        )}

        {/* Additional Information Tab */}
        {activeTab === 'additional' && additionalInfo && (
          <div className="animate-fadeIn">
            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border border-green-100">
              <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderResume('Resume', additionalInfo.resume_url)}
                {renderField('Start Date', additionalInfo.start_date)}
                {renderField('End Date', additionalInfo.end_date)}
                {renderField('Number of Applications', additionalInfo.no_of_applications)}
                {renderField('Highest Education', additionalInfo.highest_education)}
                {renderField('University', additionalInfo.university_name)}
                {renderField('GPA', additionalInfo.cumulative_gpa)}
                {renderField('Graduation Year', additionalInfo.graduation_year)}
                {renderField('Main Subject', additionalInfo.main_subject)}
                {renderField('Role', additionalInfo.role)}
                {renderField('Experience (years)', additionalInfo.experience)}
                {renderField('Work Preferences', additionalInfo.work_preferences)}
                {renderField('Alternate Job Roles', additionalInfo.alternate_job_roles)}
                {renderField('Exclude Companies', additionalInfo.exclude_companies)}
                {renderField('Gender', additionalInfo.gender)}
                {renderField('Date of Birth', additionalInfo.date_of_birth)}
                {renderField('Primary Phone', additionalInfo.primary_phone)}
                {renderField('Full Address', additionalInfo.full_address)}
                {renderField('State', additionalInfo.state_of_residence)}
                {renderField('Country', additionalInfo.zip_or_country)}
              </dl>
            </div>
          </div>
        )}

        {/* Eligibility & Authorization Tab */}
        {activeTab === 'eligibility' && additionalInfo && (
          <div className="animate-fadeIn">
            <div className="bg-gradient-to-br from-yellow-50 to-white rounded-xl p-6 border border-yellow-100">
              <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderField('Over 18', additionalInfo.is_over_18 ? 'Yes' : 'No')}
                {renderField('Eligible to Work in US', additionalInfo.eligible_to_work_in_us ? 'Yes' : 'No')}
                {renderField('Authorized Without Visa', additionalInfo.authorized_without_visa ? 'Yes' : 'No')}
                {renderField('Require Future Sponsorship', additionalInfo.require_future_sponsorship ? 'Yes' : 'No')}
                {renderField('Can Perform Essential Functions', additionalInfo.can_perform_essential_functions ? 'Yes' : 'No')}
                {renderField('Willing to Relocate', additionalInfo.willing_to_relocate ? 'Yes' : 'No')}
                {renderField('Can Work 3 Days in Office', additionalInfo.can_work_3_days_in_office ? 'Yes' : 'No')}
                {renderField('Can Provide Legal Documents', additionalInfo.can_provide_legal_docs ? 'Yes' : 'No')}
                {renderField('Willing for Background Check', additionalInfo.willing_background_check ? 'Yes' : 'No')}
                {renderField('Willing for Drug Screen', additionalInfo.willing_drug_screen ? 'Yes' : 'No')}
              </dl>
            </div>
          </div>
        )}

        {/* Links & Services Tab */}
        {activeTab === 'links' && additionalInfo && (
          <div className="animate-fadeIn">
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 border border-purple-100">
              <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {additionalInfo.linked_in_url && (
                  <div className="mb-4">
                    <dt className="text-sm font-medium text-gray-600 mb-1">LinkedIn</dt>
                    <dd>
                      <a href={additionalInfo.linked_in_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline break-all">
                        View Profile
                      </a>
                    </dd>
                  </div>
                )}
                {additionalInfo.github_url && (
                  <div className="mb-4">
                    <dt className="text-sm font-medium text-gray-600 mb-1">GitHub</dt>
                    <dd>
                      <a href={additionalInfo.github_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline break-all">
                        View Profile
                      </a>
                    </dd>
                  </div>
                )}
                {renderField('Hispanic/Latino', additionalInfo.is_hispanic_latino)}
                {renderField('Race/Ethnicity', additionalInfo.race_ethnicity)}
                {renderField('Veteran Status', additionalInfo.veteran_status)}
                {renderField('Disability Status', additionalInfo.disability_status)}
              </dl>
              <div className="mt-4">
                {renderArray('Add-ons Info', additionalInfo.add_ons_info)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function ClientDetailsModal({ data, onClose }) {
  const client = data?.client;
  const additionalInfo = data?.additional_information;

  if (!client) return null;

  const renderField = (label, value) => (
    <div className="mb-4">
      <dt className="text-sm font-medium text-gray-600 mb-1">{label}</dt>
      <dd className="text-sm text-gray-900">{value || '—'}</dd>
    </div>
  );

  const renderArray = (label, arr) => {
    if (!arr || arr.length === 0) return null;
    return (
      <div className="mb-4">
        <dt className="text-sm font-medium text-gray-600 mb-2">{label}</dt>
        <dd className="flex flex-wrap gap-1">
          {arr.map((item, idx) => (
            <span key={idx} className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
              {item}
            </span>
          ))}
        </dd>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-gray-900/50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white w-full max-w-6xl rounded-2xl shadow-2xl border border-gray-200 my-8">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Client Details - {client.applywizz_id}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Client Information */}
          <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-6 border border-indigo-100">
            <h3 className="text-lg font-semibold text-indigo-900 mb-4 flex items-center gap-2">
              <span>👤</span> Client Information
            </h3>
            <dl>
              {renderField('Full Name', client.full_name)}
              {renderField('Personal Email', client.personal_email)}
              {renderField('Company Email', client.company_email)}
              {renderField('WhatsApp', client.whatsapp_number)}
              {renderField('Phone', client.callable_phone)}
              {renderField('Visa Type', client.visa_type)}
              {renderField('Sponsorship', client.sponsorship ? 'Yes' : 'No')}
              {renderField('Work Authorization', client.work_auth_details)}
              {renderArray('Job Role Preferences', client.job_role_preferences)}
              {renderArray('Location Preferences', client.location_preferences)}
              {renderField('Salary Range', client.salary_range)}
              {renderField('Created At', new Date(client.created_at).toLocaleString())}
            </dl>
          </div>

          {/* Additional Information */}
          {additionalInfo && (
            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border border-green-100">
              <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                <span>📋</span> Additional Information
              </h3>
              <dl>
                {renderField('Resume URL', additionalInfo.resume_url)}
                {renderField('Start Date', additionalInfo.start_date)}
                {renderField('End Date', additionalInfo.end_date)}
                {renderField('Number of Applications', additionalInfo.no_of_applications)}
                {renderField('Highest Education', additionalInfo.highest_education)}
                {renderField('University', additionalInfo.university_name)}
                {renderField('GPA', additionalInfo.cumulative_gpa)}
                {renderField('Graduation Year', additionalInfo.graduation_year)}
                {renderField('Main Subject', additionalInfo.main_subject)}
                {renderField('Role', additionalInfo.role)}
                {renderField('Experience (years)', additionalInfo.experience)}
                {renderField('Work Preferences', additionalInfo.work_preferences)}
                {renderField('Alternate Job Roles', additionalInfo.alternate_job_roles)}
                {renderField('Exclude Companies', additionalInfo.exclude_companies)}
                {renderField('Gender', additionalInfo.gender)}
                {renderField('Date of Birth', additionalInfo.date_of_birth)}
                {renderField('Primary Phone', additionalInfo.primary_phone)}
                {renderField('Full Address', additionalInfo.full_address)}
                {renderField('State', additionalInfo.state_of_residence)}
                {renderField('Country', additionalInfo.zip_or_country)}
              </dl>
            </div>
          )}

          {/* Work Authorization Details */}
          {additionalInfo && (
            <div className="bg-gradient-to-br from-yellow-50 to-white rounded-xl p-6 border border-yellow-100">
              <h3 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center gap-2">
                <span>✅</span> Eligibility & Authorization
              </h3>
              <dl>
                {renderField('Over 18', additionalInfo.is_over_18 ? 'Yes' : 'No')}
                {renderField('Eligible to Work in US', additionalInfo.eligible_to_work_in_us ? 'Yes' : 'No')}
                {renderField('Authorized Without Visa', additionalInfo.authorized_without_visa ? 'Yes' : 'No')}
                {renderField('Require Future Sponsorship', additionalInfo.require_future_sponsorship ? 'Yes' : 'No')}
                {renderField('Can Perform Essential Functions', additionalInfo.can_perform_essential_functions ? 'Yes' : 'No')}
                {renderField('Willing to Relocate', additionalInfo.willing_to_relocate ? 'Yes' : 'No')}
                {renderField('Can Work 3 Days in Office', additionalInfo.can_work_3_days_in_office ? 'Yes' : 'No')}
                {renderField('Can Provide Legal Documents', additionalInfo.can_provide_legal_docs ? 'Yes' : 'No')}
                {renderField('Willing for Background Check', additionalInfo.willing_background_check ? 'Yes' : 'No')}
                {renderField('Willing for Drug Screen', additionalInfo.willing_drug_screen ? 'Yes' : 'No')}
              </dl>
            </div>
          )}

          {/* Additional Details */}
          {additionalInfo && (
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 border border-purple-100">
              <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <span>🔗</span> Links & Services
              </h3>
              <dl>
                {additionalInfo.linked_in_url && (
                  <div className="mb-4">
                    <dt className="text-sm font-medium text-gray-600 mb-1">LinkedIn</dt>
                    <dd>
                      <a href={additionalInfo.linked_in_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline break-all">
                        {additionalInfo.linked_in_url}
                      </a>
                    </dd>
                  </div>
                )}
                {additionalInfo.github_url && (
                  <div className="mb-4">
                    <dt className="text-sm font-medium text-gray-600 mb-1">GitHub</dt>
                    <dd>
                      <a href={additionalInfo.github_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline break-all">
                        {additionalInfo.github_url}
                      </a>
                    </dd>
                  </div>
                )}
                {renderArray('Add-ons Info', additionalInfo.add_ons_info)}
                {renderField('Hispanic/Latino', additionalInfo.is_hispanic_latino)}
                {renderField('Race/Ethnicity', additionalInfo.race_ethnicity)}
                {renderField('Veteran Status', additionalInfo.veteran_status)}
                {renderField('Disability Status', additionalInfo.disability_status)}
              </dl>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-end">
          <button onClick={onClose} className="px-6 py-2 rounded-md bg-indigo-600 text-white font-medium shadow hover:bg-indigo-700">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
